import { Controller } from "@hotwired/stimulus";

const laneSelector = "[data-task-lane]";
const listSelector = "[data-task-list]";
const cardSelector = "[data-task-card]";
const toggleSelector = "[data-lane-toggle]";
const emptyMessageSelector = "[data-empty-message]";

export default class extends Controller<HTMLElement> {
  static values = { workspaceSlug: String };

  declare readonly workspaceSlugValue: string;
  declare readonly hasWorkspaceSlugValue: boolean;

  private abortController?: AbortController;
  private draggedCard?: HTMLElement;
  private dropIndicator?: HTMLElement;
  private sourceLane?: HTMLElement;

  connect() {
    this.abortController = new AbortController();
    this.initializeLanes();
    this.initializeCards();
  }

  disconnect() {
    this.abortController?.abort();
  }

  private initializeLanes() {
    const collapsed = this.collapsedLanes();

    this.lanes.forEach((lane) => {
      const status = lane.dataset.status;
      this.setLaneCollapsed(lane, Boolean(status && collapsed.has(status)));
      this.updateEmptyState(lane);

      lane.querySelector<HTMLElement>(toggleSelector)?.addEventListener("click", () => {
        const nextCollapsed = !lane.classList.contains("collapsed");
        this.setLaneCollapsed(lane, nextCollapsed);

        if (status) {
          nextCollapsed ? collapsed.add(status) : collapsed.delete(status);
          this.saveCollapsedLanes(collapsed);
        }

        this.updateBoardColumns();
      }, this.listenerOptions);

      lane.addEventListener("dragover", this.dragOverLane, this.listenerOptions);
      lane.addEventListener("dragleave", this.dragLeaveLane, this.listenerOptions);
      lane.addEventListener("drop", this.dropCard, this.listenerOptions);
    });

    this.updateBoardColumns();
  }

  private initializeCards() {
    this.cards.forEach((card) => {
      card.addEventListener("dragstart", this.dragStartCard, this.listenerOptions);
      card.addEventListener("dragend", this.dragEndCard, this.listenerOptions);
    });
  }

  private dragStartCard = (event: DragEvent) => {
    const card = event.currentTarget as HTMLElement;

    this.draggedCard = card;
    this.sourceLane = card.closest<HTMLElement>(laneSelector) ?? undefined;
    card.classList.add("dragging");

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", card.dataset.taskId ?? "");
    }
  };

  private dragEndCard = () => {
    this.draggedCard?.classList.remove("dragging");
    this.lanes.forEach((lane) => lane.classList.remove("drop-target"));
    this.removeDropIndicator();
    this.draggedCard = undefined;
    this.sourceLane = undefined;
  };

  private dragOverLane = (event: DragEvent) => {
    if (!this.draggedCard) return;

    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
    const lane = event.currentTarget as HTMLElement;
    const targetList = lane.querySelector<HTMLElement>(listSelector);

    lane.classList.add("drop-target");
    if (targetList) this.showDropIndicator(targetList, event);
  };

  private dragLeaveLane = (event: DragEvent) => {
    const lane = event.currentTarget as HTMLElement;
    if (!event.relatedTarget || !lane.contains(event.relatedTarget as Node)) {
      lane.classList.remove("drop-target");
      this.removeDropIndicator();
    }
  };

  private dropCard = async (event: DragEvent) => {
    event.preventDefault();

    const lane = event.currentTarget as HTMLElement;
    lane.classList.remove("drop-target");
    if (!this.draggedCard) return;

    const targetList = lane.querySelector<HTMLElement>(listSelector);
    const originalList = this.sourceLane?.querySelector<HTMLElement>(listSelector);
    const originalNextSibling = this.draggedCard.nextElementSibling;
    if (!targetList) return;

    if (lane.classList.contains("collapsed")) {
      this.setLaneCollapsed(lane, false);
      this.persistLaneCollapsed(lane.dataset.status, false);
      this.updateBoardColumns();
    }

    targetList.insertBefore(this.draggedCard, this.dropReferenceCard(targetList, event));
    this.removeDropIndicator();
    this.updateEmptyState(lane);
    if (this.sourceLane) this.updateEmptyState(this.sourceLane);

    try {
      await this.moveCard(this.draggedCard, lane);
    } catch (_error) {
      if (originalList) originalList.insertBefore(this.draggedCard, originalNextSibling);
      this.updateEmptyState(lane);
      if (this.sourceLane) this.updateEmptyState(this.sourceLane);
    }
  };

  private async moveCard(card: HTMLElement, lane: HTMLElement) {
    const response = await fetch(card.dataset.moveUrl ?? "", {
      method: "PATCH",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken()
      },
      body: JSON.stringify({ status: lane.dataset.status })
    });

    if (!response.ok) throw new Error("Task move failed");
  }

  private dropReferenceCard(list: HTMLElement, event: DragEvent): HTMLElement | null {
    const cards = Array.from(list.querySelectorAll<HTMLElement>(cardSelector)).filter((card) => (
      card !== this.draggedCard
    ));

    return cards.reduce<{ offset: number; element: HTMLElement | null }>((closest, card) => {
      const box = card.getBoundingClientRect();
      const offset = event.clientY - box.top - box.height / 2;

      if (offset < 0 && offset > closest.offset) {
        return { offset, element: card };
      }

      return closest;
    }, { offset: Number.NEGATIVE_INFINITY, element: null }).element;
  }

  private showDropIndicator(list: HTMLElement, event: DragEvent) {
    const indicator = this.ensureDropIndicator();
    const referenceCard = this.dropReferenceCard(list, event);
    const top = referenceCard ? this.dropLineBeforeCard(list, referenceCard) : this.dropLineAtListEnd(list);

    indicator.style.top = `${top}px`;
    list.appendChild(indicator);
  }

  private ensureDropIndicator() {
    if (!this.dropIndicator) {
      this.dropIndicator = document.createElement("div");
      this.dropIndicator.className = "task-drop-indicator";
      this.dropIndicator.setAttribute("aria-hidden", "true");
    }

    return this.dropIndicator;
  }

  private removeDropIndicator() {
    this.dropIndicator?.remove();
  }

  private dropLineBeforeCard(list: HTMLElement, card: HTMLElement) {
    return card.getBoundingClientRect().top - list.getBoundingClientRect().top + list.scrollTop - 6;
  }

  private dropLineAtListEnd(list: HTMLElement) {
    const cards = Array.from(list.querySelectorAll<HTMLElement>(cardSelector)).filter((card) => (
      card !== this.draggedCard
    ));
    const lastCard = cards.at(-1);

    return lastCard
      ? lastCard.getBoundingClientRect().bottom - list.getBoundingClientRect().top + list.scrollTop + 6
      : 12;
  }

  private updateEmptyState(lane: HTMLElement) {
    const cards = lane.querySelectorAll(cardSelector);
    const empty = lane.querySelector<HTMLElement>(emptyMessageSelector);
    const count = lane.querySelector<HTMLElement>(".lane-count");

    if (empty) empty.hidden = cards.length > 0;
    if (count) count.textContent = String(cards.length);
  }

  private setLaneCollapsed(lane: HTMLElement, collapsed: boolean) {
    lane.classList.toggle("collapsed", collapsed);
    lane.querySelector<HTMLElement>(toggleSelector)?.setAttribute("aria-expanded", String(!collapsed));
  }

  private updateBoardColumns() {
    const columns = this.lanes.map((lane) => (
      lane.classList.contains("collapsed") ? "56px" : "minmax(0, 1fr)"
    ));

    this.element.style.setProperty("--lane-columns", columns.join(" "));
  }

  private collapsedLanes() {
    try {
      return new Set<string>(JSON.parse(window.localStorage.getItem(this.collapsedLanesStorageKey) || "[]"));
    } catch (_error) {
      return new Set<string>();
    }
  }

  private saveCollapsedLanes(lanes: Set<string>) {
    window.localStorage.setItem(this.collapsedLanesStorageKey, JSON.stringify([...lanes]));
  }

  private persistLaneCollapsed(status: string | undefined, collapsed: boolean) {
    if (!status) return;

    const lanes = this.collapsedLanes();
    collapsed ? lanes.add(status) : lanes.delete(status);
    this.saveCollapsedLanes(lanes);
  }

  private csrfToken() {
    return document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content ?? "";
  }

  private get collapsedLanesStorageKey() {
    const workspace = this.hasWorkspaceSlugValue ? this.workspaceSlugValue : "global";
    return `workstream.collapsedLanes.${workspace}`;
  }

  private get lanes() {
    return Array.from(this.element.querySelectorAll<HTMLElement>(laneSelector));
  }

  private get cards() {
    return Array.from(this.element.querySelectorAll<HTMLElement>(cardSelector));
  }

  private get listenerOptions() {
    return { signal: this.abortController?.signal };
  }
}
